import * as React from "react";
import { getSupportUrl } from "../../utils/supportUrl";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useNavigate } from "react-router-dom";
import { Autocomplete, TextField, Typography } from "@mui/material";
import { getCustomerWebhooks } from "./EditCameraUtils/getCustomerWebhooks";
import { getNumbersAfterSites } from "./EditCameraUtils/urlUtils";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";

export const AddStreamWebhookDialog = ({
  setAddStreamWebhookDialogOpen,
  addStreamWebhookDialogOpen,
  cameraGeneralInfo,
}: {
  setAddStreamWebhookDialogOpen: any;
  addStreamWebhookDialogOpen: any;
  cameraGeneralInfo: any;
}) => {
  const {
    setBackdropLoadingScreenEditCameraOn,
    handleSubmitNewStreamWebhook,
    cameraStreams,
  } = useEditCameraStore();
  const navigate = useNavigate();

  const [postObject, setPostObject] = React.useState({
    webhook: null,
    streams: [],
    custom_fields: "{}",
    event_types: [],
  });

  const [errorState, setErrorState] = React.useState({
    webhook: true,
    custom_fields: false,
  });

  const [customerWebhooks, setCustomerWebhooks] = React.useState<any[]>([]);
  const [webhooksLoaded, setWebhooksLoaded] = React.useState(false);

  const webHookEventTypeOptions = JSON.parse(
    localStorage.webHookEventTypeOptions
  );

  const webHookEventTypeNames = new Map();
  webHookEventTypeOptions.forEach((option: any) => {
    webHookEventTypeNames.set(option.name, option.id);
  });

  const streamNames = new Map();
  cameraStreams?.forEach((stream: any) => {
    streamNames.set(`${stream.name} (${stream.product})`, stream.id);
  });

  React.useEffect(() => {
    getCustomerWebhooks(navigate, setCustomerWebhooks, () =>
      setWebhooksLoaded(true)
    );
  }, []);

  React.useEffect(() => {
    if (cameraStreams && cameraStreams.length === 1) {
      setPostObject((previousState: any) => {
        return {
          ...previousState,
          streams: [cameraStreams[0].id],
        };
      });
    }
  }, [customerWebhooks]);

  return (
    <Dialog
      open={addStreamWebhookDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ textAlign: "center" }}>
        Add Stream Webhook{" "}
      </DialogTitle>
      <DialogContent>
        <FormLabel component="legend" sx={{ paddingBottom: 1 }}>
          {" "}
          Webhook*
        </FormLabel>
        {/* Options are the webhook objects, keyed by id: two site webhooks
            with the same display name must stay distinct (ENG-2233). */}
        <Autocomplete
          id="webhook"
          options={customerWebhooks}
          getOptionLabel={(option: any) => option?.display_name ?? ""}
          isOptionEqualToValue={(option: any, value: any) =>
            option?.id === value?.id
          }
          sx={{ width: 400 }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, "aria-label": "Webhook" }}
            />
          )}
          onChange={(event, value: any) => {
            setPostObject((previousState: any) => {
              return {
                ...previousState,
                webhook: value ? value.id : null,
              };
            });
            setErrorState((previousState: any) => {
              return { ...previousState, webhook: value === null };
            });
          }}
        />
        {webhooksLoaded && customerWebhooks.length === 0 && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "#283E6D" }}
            data-testid="no-site-webhooks"
          >
            This site has no webhooks yet. Add one on the site&apos;s{" "}
            <a
              href={`/sites/aboutsite/${getNumbersAfterSites(location.href)}`}
              onClick={(event) => {
                event.preventDefault();
                navigate(
                  `/sites/aboutsite/${getNumbersAfterSites(location.href)}`
                );
              }}
              style={{ color: "#1976d2", textDecoration: "none" }}
            >
              Webhooks tab
            </a>
            , then come back to attach it to this camera.
          </Typography>
        )}
        <br></br>
        {cameraStreams && cameraStreams.length > 1 && (
          <div>
            <FormLabel component="legend" sx={{ paddingBottom: 1 }}>
              {" "}
              Streams*
            </FormLabel>
            <Autocomplete
              id="streams"
              multiple
              options={cameraStreams.map((object: any) => {
                return `${object.name} (${object.product})`;
              })}
              sx={{ width: 400 }}
              renderInput={(params) => <TextField {...params} />}
              onChange={(event, value) => {
                setPostObject((previousState: any) => {
                  return {
                    ...previousState,
                    streams: value.map((object: any) => {
                      return streamNames.get(object);
                    }),
                  };
                });
              }}
            />
            <br></br>
          </div>
        )}
        <Box sx={{ display: "flex" }}>
          <FormControl variant="standard">
            <FormLabel component="legend">Event Types</FormLabel>
            <FormGroup>
              {webHookEventTypeOptions.map((option: any) => {
                return (
                  <FormControlLabel
                    key={option.name}
                    control={
                      <Checkbox
                        onChange={(event) => {
                          if (event.target.checked) {
                            setPostObject((previousState: any) => {
                              return {
                                ...previousState,
                                event_types: [
                                  ...previousState.event_types,
                                  webHookEventTypeNames.get(option.name),
                                ],
                              };
                            });
                          } else {
                            setPostObject((previousState: any) => {
                              return {
                                ...previousState,
                                event_types: previousState.event_types.filter(
                                  (eventType: any) =>
                                    eventType !==
                                    webHookEventTypeNames.get(option.name)
                                ),
                              };
                            });
                          }
                        }}
                        name={option.name}
                      />
                    }
                    label={option.name}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        </Box>
        <br></br>
        <FormLabel component="legend" sx={{ paddingBottom: 1 }}>
          {" "}
          Custom Fields
        </FormLabel>
        <Box>
          <TextField
            sx={{ width: 550 }}
            multiline
            rows={7}
            value={postObject.custom_fields}
            error={errorState.custom_fields}
            onChange={(event) => {
              const inputValue = event.target.value;

              // Update postObject with the current input value
              setPostObject((previousState: any) => ({
                ...previousState,
                custom_fields: inputValue,
              }));

              // Validate JSON
              try {
                JSON.parse(inputValue);
                setErrorState((previousState: any) => ({
                  ...previousState,
                  custom_fields: false,
                }));
              } catch (e) {
                setErrorState((previousState: any) => ({
                  ...previousState,
                  custom_fields: true,
                }));
              }
            }}
            helperText={
              errorState.custom_fields
                ? "Invalid JSON format. Ensure your input is a valid JSON object."
                : ""
            }
          />

          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            Enter a valid JSON with custom fields that should be sent
            specifically for this camera. See formatting examples in our support
            page:{" "}
            <a
              href={getSupportUrl("/admin_ui/webhooks/")}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "none" }}
            >
              Webhooks
            </a>
          </Typography>
        </Box>
        <br></br>
        <br></br>
        <FormLabel component="legend"> *Required</FormLabel>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton
          onClick={() => {
            setAddStreamWebhookDialogOpen(false);
          }}
          text="Cancel"
          disabled={false}
        />
        <OrangeTextButton
          onClick={() => {
            try {
              const parsedCustomFields = JSON.parse(postObject.custom_fields);

              // Prepare the postObject for API submission
              const payload = {
                ...postObject,
                custom_fields: parsedCustomFields, // Pass the parsed object
              };
              setBackdropLoadingScreenEditCameraOn(true);
              setAddStreamWebhookDialogOpen(false);
              handleSubmitNewStreamWebhook(
                payload,
                navigate,
                cameraGeneralInfo.id
              );
            } catch (error) {
              alert("Invalid JSON, cannot submit.");
            }
          }}
          text="Submit"
          disabled={
            errorState.webhook ||
            errorState.custom_fields ||
            postObject.streams.length === 0
          }
        />
      </DialogActions>
    </Dialog>
  );
};
